import gql from 'graphql-tag';
import {useLazyQuery, useMutation, useQuery} from '@apollo/client';
import {useMemo} from 'react';
import useAccountId from '../../hooks/useAccountId';
import {GetClientFilesV2} from '../../schema-types/GetClientFilesV2';
import {
  UploadClientDocumentV2,
  UploadClientDocumentV2Variables,
} from '../../schema-types/UploadClientDocumentV2';
import {
  ClientDocumentDownloadV2,
  ClientDocumentDownloadV2Variables,
} from '../../schema-types/ClientDocumentDownloadV2';

export const GET_CLIENT_FILES_V2 = gql`
  query GetClientFilesV2($accountId: UUID!) {
    clientFiles(accountId: $accountId) {
      documentId
      name
      url
      createdAt
      fileType
      publishPortal
      documentType
    }
  }
`;

export const DOWNLOAD_CLIENT_FILE_V2 = gql`
  query ClientDocumentDownloadV2($accountCuroId: UUID!, $documentId: String!) {
    clientDocumentDownload(accountCuroId: $accountCuroId, documentId: $documentId) {
      ok
      content
      contentType
      errors
    }
  }
`;

export const UPLOAD_CLIENT_FILE_V2 = gql`
  mutation UploadClientDocumentV2(
    $accountId: UUID!
    $contactId: UUID
    $datePicked: DateTime!
    $documentGroup: DocumentGroup!
    $fileData: String!
    $notes: String!
    $potId: UUID
    $providerId: UUID
    $secondContactId: UUID
    $taxYear: TaxYearInput
    $planNumberStatus: DocumentUploadPlanNumberStatus!
  ) {
    clientDocumentUploadV2(
      accountId: $accountId
      contactId: $contactId
      datePicked: $datePicked
      documentGroup: $documentGroup
      fileData: $fileData
      notes: $notes
      potId: $potId
      providerId: $providerId
      secondContactId: $secondContactId
      taxYear: $taxYear
      planNumberStatus: $planNumberStatus
    ) {
      ok
      errors
    }
  }
`;

export const useClientFilesV2 = () => {
  const {accountId} = useAccountId();
  const {
    data,
    error: clientFilesError,
    loading: clientFilesLoading,
  } = useQuery<GetClientFilesV2>(GET_CLIENT_FILES_V2, {
    variables: {
      accountId,
    },
  });

  const clientFiles = useMemo(() => {
    return (
      data?.clientFiles?.map((f) => {
        return {
          id: f.documentId,
          ...f,
        };
      }) || []
    );
  }, [data]);

  const clientFilesPDFOnly = useMemo(() => {
    return (
      data?.clientFiles
        ?.filter((f) => f.fileType === 'application/pdf' && f.publishPortal)
        .map((f) => {
          return {
            ...f,
            id: f.name,
            // removing the file extension from the name
            name: f.name?.replaceAll('.pdf', '').replaceAll('.PDF', ''),
          };
        }) || []
    );
  }, [data]);

  const [uploadClientFile, {loading: uploadClientFileLoading}] = useMutation<
    UploadClientDocumentV2,
    UploadClientDocumentV2Variables
  >(UPLOAD_CLIENT_FILE_V2, {
    refetchQueries: [
      {
        query: GET_CLIENT_FILES_V2,
        variables: {
          accountId,
        },
      },
    ],
  });

  const [
    downloadClientFile,
    {loading: downloadClientFileLoading, data: downloadClientFileData},
  ] = useLazyQuery<ClientDocumentDownloadV2, ClientDocumentDownloadV2Variables>(
    DOWNLOAD_CLIENT_FILE_V2
  );

  return {
    clientFiles,
    clientFilesError,
    clientFilesLoading,
    clientFilesPDFOnly,
    uploadClientFile,
    uploadClientFileLoading,
    downloadClientFile,
    downloadClientFileLoading,
    downloadClientFileData,
  };
};
